import * as React from 'react';
import Checkbox from '../../../BaseComponents/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import cn from 'classnames';
import { useTheme } from '../../../theme';
import {
	AviaSegmentGroup,
	Flight,
	isCheckinFlightGuard,
	isCheckinSegmentGuard,
	TransferInfoBySegment
} from '../../store';
import { SelectableCheckoutSegment } from '../SelectableSegment/SelectableSegment';
import { useCallback } from 'react';
import { CheckinSegmentFragment, SegmentFragment } from '@websky/graphql';
import { Fade } from '@mui/material';
import { SelectableCheckinSegment } from '../SelectableSegment/CheckinSelectableSegment';

export interface Props {
	flights: Flight[];
	selectedSegments: string[];
	onChange?: React.Dispatch<string>; // "segmentId"
	type: 'select' | 'review';
	transferInfo?: TransferInfoBySegment;
}

const FlightSelector: React.FC<Props> = ({ flights, selectedSegments, onChange, type, transferInfo }) => {
	const { FlightSelector: css, SelectableSegment: selectableSegmentStyles } = useTheme('UpgradeFlightClass');

	const handleToggle = useCallback(
		(segmentId: string) => {
			if (type === 'select' && onChange) {
				onChange(segmentId);
			}
		},
		[selectedSegments, onChange]
	);

	const renderSegment = useCallback(
		(segment: SegmentFragment | CheckinSegmentFragment, index: number, flight?: Flight) => {
			let isMultipleSegmentsOnFlightSelected: boolean;

			if (flight) {
				isMultipleSegmentsOnFlightSelected =
					(flight as AviaSegmentGroup).segments.filter(flightSegment =>
						selectedSegments.includes(flightSegment.id)
					)?.length > 1;
			}

			return (
				<Fade key={segment.id} in={type === 'select' || selectedSegments.includes(segment.id)} unmountOnExit>
					<div key={segment.id}>
						<FormControlLabel
							className={css.formControl}
							classes={{
								label: css.label
							}}
							control={
								type === 'select' ? (
									<Checkbox
										defaultChecked={selectedSegments.includes(segment.id)}
										onChange={() => handleToggle(segment.id)}
										className={css.checkbox}
									/>
								) : (
									<div />
								)
							}
							label={
								isCheckinSegmentGuard(segment) ? (
									<SelectableCheckinSegment
										showStopsInfo={type === 'select' || isMultipleSegmentsOnFlightSelected}
										segment={segment}
										index={index}
										highlight={type === 'select' && selectedSegments.includes(segment.id)}
										parentFlight={{
											id: null,
											price: null,
											flightNumber: null,
											arrival: null,
											departure: null,
											duration: null,
											stops: [],
											operatingAirline: null,
											marketingAirline: null,
											checkinInfo: null,
											checkinWithSegments: [],
											segments: [
												{
													...segment,
													messages: []
												}
											] // todo: write adapter
										}}
										transferInfo={transferInfo[segment.id]}
									/>
								) : (
									<SelectableCheckoutSegment
										showStopsInfo={type === 'select' || isMultipleSegmentsOnFlightSelected}
										segment={segment}
										index={index}
										highlight={type === 'select' && selectedSegments.includes(segment.id)}
										parentFlight={flight}
										transferInfo={transferInfo[segment.id]}
									/>
								)
							}
						/>
					</div>
				</Fade>
			);
		},
		[selectedSegments, flights, type, transferInfo]
	);

	if (isCheckinFlightGuard(flights[0])) {
		return (
			<div
				className={cn(selectableSegmentStyles.flight, {
					[selectableSegmentStyles.transfer]: flights.length > 1,
					[selectableSegmentStyles.selectType]: type === 'select'
				})}
			>
				{flights.map((segment, index) => renderSegment(segment as CheckinSegmentFragment, index, null))}
			</div>
		);
	} else {
		return (
			<>
				{flights.map(flight => {
					const aviaFlight = flight as AviaSegmentGroup;

					return (
						<div
							key={aviaFlight.flightId + aviaFlight.groupId}
							className={cn(selectableSegmentStyles.flight, {
								[selectableSegmentStyles.transfer]: aviaFlight.segments.length > 1,
								[selectableSegmentStyles.selectType]: type === 'select'
							})}
						>
							{aviaFlight.segments.map((segment, index) => renderSegment(segment, index, flight))}
						</div>
					);
				})}
			</>
		);
	}
};

export default FlightSelector;
